/**
 * Registry des indicateurs visibles dans le cockpit.
 *
 * Source unique de vérité pour le bandeau d'indicateurs ET pour la
 * sheet de détail qui s'ouvre au tap. Ajouter un indicateur = une entrée
 * ici, rien d'autre à toucher côté UI.
 */

import type { DerivedAggregates, WorldState } from "./types";

export type TrendDirection = "up" | "down" | "flat";

/**
 * Lecture qualitative d'un indicateur — sépare la valeur numérique de
 * l'évaluation politique. Tu vois le chiffre puis le mot, et le mot te
 * dit *si* c'est bien.
 */
export type Verdict =
  "excellent" | "sain" | "tenable" | "preoccupant" | "critique";

export const VERDICT_LABEL: Record<Verdict, string> = {
  excellent: "Excellent",
  sain: "Sain",
  tenable: "Tenable",
  preoccupant: "Préoccupant",
  critique: "Critique",
};

export interface IndicatorDef {
  id: string;
  label: string;
  unit: string;
  description: string;
  extract: (state: WorldState, aggregates: DerivedAggregates) => number;
  format: (value: number) => string;
  alertAbove?: number;
  alertBelow?: number;
  /**
   * G4 — onboarding visuel progressif. Tour minimum à partir duquel
   * l'indicateur est visible dans le bandeau du cockpit. Avant ce tour,
   * il est *absent* (pas masqué). Le but est d'éviter le mur d'info à T0.
   * Les 3 indicateurs structurants (approval, gdp, budget) restent à 0.
   */
  revealedFromTurn?: number;
  /**
   * E2 — verdict qualitatif. Reçoit la valeur brute, retourne un mot
   * (5 niveaux). C'est ce qui rend l'indicateur lisible sans formation
   * d'économiste.
   */
  verdict?: (value: number) => Verdict;
}

export const INDICATORS: IndicatorDef[] = [
  {
    id: "approval",
    label: "Approbation",
    unit: "%",
    format: (v) => v.toFixed(0),
    description:
      "Approbation mesurée par les sondages — distincte du ressenti réel des cohortes. Combine la satisfaction de la population, l'image médiatique (Le Monde, etc.) et le régime (la dictature truque). Sous 35 %, alerte politique.",
    extract: (state, agg) => state.polity.approval_rate ?? agg.meanSatisfaction,
    alertBelow: 35,
    verdict: (v) =>
      v >= 60
        ? "excellent"
        : v >= 50
          ? "sain"
          : v >= 40
            ? "tenable"
            : v >= 30
              ? "preoccupant"
              : "critique",
  },
  {
    id: "gdp",
    label: "PIB",
    unit: " Md€",
    format: (v) => (v / 1e9).toFixed(0),
    description:
      "Produit intérieur brut — somme des valeurs ajoutées de tous les secteurs de l'économie (agriculture, industrie, services, numérique, énergie, finance).",
    extract: (state) => state.economy.gdp,
    // Onboarding progressif (2026-05-22) : le PIB apparaît à T2 quand
    // l'INSEE confirme la croissance trimestrielle. Avant ce tour, le
    // joueur reste concentré sur l'approbation et le budget.
    revealedFromTurn: 2,
    // Pas de verdict pour le PIB seul — sa lecture est dans la croissance,
    // qui sera abordée dans une passe ultérieure.
  },
  {
    id: "budget",
    label: "Budget de l'État",
    unit: " Md€",
    format: (v) => (v / 1e9).toFixed(0),
    description:
      "Différence entre les recettes fiscales (IR, IS, TVA, etc.) et les dépenses publiques (santé, éducation, défense, social, investissement, service de la dette). Négatif = déficit qui creuse la dette.",
    // Onboarding progressif : le budget apparaît à T1 — Bercy publie
    // sa première synthèse pour le nouveau gouvernement.
    revealedFromTurn: 1,
    extract: (state) => {
      const revenues = Object.values(state.economy.budget.revenues).reduce(
        (a, b) => a + b,
        0,
      );
      const expenses = Object.values(state.economy.budget.expenses).reduce(
        (a, b) => a + b,
        0,
      );
      return revenues - expenses;
    },
    alertBelow: -50e9,
    verdict: (v) => {
      // Seuils en Md€. Excédent rare → excellent. Déficit > 100 Md = critique.
      const billions = v / 1e9;
      if (billions >= 0) return "excellent";
      if (billions >= -30) return "sain";
      if (billions >= -60) return "tenable";
      if (billions >= -100) return "preoccupant";
      return "critique";
    },
  },
  {
    id: "inflation",
    label: "Inflation",
    unit: " %",
    format: (v) => (v * 100).toFixed(1),
    description:
      "Taux annualisé d'évolution des prix. Au-delà de 5 %, les ménages modestes décrochent.",
    extract: (state) => state.economy.inflation,
    alertAbove: 0.05,
    // Onboarding progressif : inflation revealed T4 (Insee publie sa
    // 1ère note conjoncturelle). Avant T4, le débat public ne porte
    // pas encore dessus.
    revealedFromTurn: 4,
    verdict: (v) =>
      v <= 0.02
        ? "excellent"
        : v <= 0.03
          ? "sain"
          : v <= 0.045
            ? "tenable"
            : v <= 0.06
              ? "preoccupant"
              : "critique",
  },
  {
    id: "unemployment",
    label: "Chômage",
    unit: " %",
    format: (v) => (v * 100).toFixed(1),
    description:
      "Part des actifs sans emploi. Dérivé de la CSP 'Unemployed' rapportée à l'ensemble des actifs de la population.",
    extract: (state) => state.economy.unemployment_rate,
    alertAbove: 0.12,
    revealedFromTurn: 3,
    verdict: (v) =>
      v <= 0.05
        ? "excellent"
        : v <= 0.075
          ? "sain"
          : v <= 0.1
            ? "tenable"
            : v <= 0.13
              ? "preoccupant"
              : "critique",
  },
  {
    id: "debt_ratio",
    label: "Dette publique",
    unit: " %",
    format: (v) => v.toFixed(0),
    description:
      "Ratio de la dette publique rapportée au PIB. Au-delà de 130 %, le service de la dette commence à dévorer le budget.",
    extract: (state) =>
      (state.economy.public_debt / Math.max(state.economy.gdp, 1)) * 100,
    alertAbove: 130,
    revealedFromTurn: 5,
    verdict: (v) =>
      v <= 80
        ? "excellent"
        : v <= 100
          ? "sain"
          : v <= 120
            ? "tenable"
            : v <= 140
              ? "preoccupant"
              : "critique",
  },
  {
    id: "population",
    label: "Population",
    unit: " M",
    format: (v) => (v / 1e6).toFixed(2),
    description:
      "Nombre total d'habitants. Évolue lentement avec la confiance dans les institutions : une population qui croit en l'avenir fait des enfants, la défiance installe l'hiver démographique. Indicateur d'ambiance long terme — quelques dizaines de milliers d'habitants par an, pas un levier.",
    extract: (_state, agg) => agg.totalPopulation,
    // Réintroduite le 2026-06-10 avec la démographie de confiance (R10) :
    // l'indicateur était statique depuis le MVP et avait été retiré.
    revealedFromTurn: 4,
  },
  {
    id: "currency",
    label: "Force de l'euro",
    unit: "",
    format: (v) => v.toFixed(0),
    description:
      "Force relative de la monnaie sur un panier d'échange (0 à 100). Une monnaie faible renchérit les imports et alimente l'inflation importée.",
    extract: (state) => state.economy.currency_strength,
    alertBelow: 70,
    // Onboarding progressif : monnaie revealed T6, alignée avec les
    // indicateurs géopolitiques. Le joueur découvre la dimension
    // internationale en bloc.
    revealedFromTurn: 6,
    verdict: (v) =>
      v >= 95
        ? "excellent"
        : v >= 85
          ? "sain"
          : v >= 75
            ? "tenable"
            : v >= 65
              ? "preoccupant"
              : "critique",
  },
  // V4 — indicateurs géopolitiques calculés runtime (mêmes formules que
  // le moteur Rust H2). Apparaissent à partir du tour 6 — moment où la
  // géopolitique commence à se révéler en jeu.
  {
    id: "security_climate",
    label: "Sécurité ressentie",
    unit: "/100",
    format: (v) => v.toFixed(0),
    description:
      "Synthèse perçue de la sécurité publique : pondère satisfaction, chômage, inflation et tensions géopolitiques. Chute brusque sur attentat ou crise sociale.",
    extract: (state, agg) => {
      // Miroir EXACT de check_security_climate_revelation (engine/mod.rs)
      // — seuils 0.08/0.03. Avant le 2026-06-10 le front utilisait
      // 0.06/0.02 : il affichait une valeur plus sévère que celle que le
      // RevelationTracker observe côté moteur.
      const sat = agg.meanSatisfaction;
      const unemp = state.economy.unemployment_rate;
      const infl = state.economy.inflation;
      const unempExcess = Math.max(0, unemp - 0.08);
      const inflExcess = Math.max(0, infl - 0.03);
      const v = 50 + (sat - 50) * 0.6 - unempExcess * 200 - inflExcess * 100;
      return Math.max(0, Math.min(100, v));
    },
    alertBelow: 35,
    revealedFromTurn: 6,
    verdict: (v) =>
      v >= 70
        ? "excellent"
        : v >= 55
          ? "sain"
          : v >= 40
            ? "tenable"
            : v >= 25
              ? "preoccupant"
              : "critique",
  },
  {
    id: "soft_power_index",
    label: "Rayonnement",
    unit: "/100",
    format: (v) => v.toFixed(0),
    description:
      "Rayonnement diplomatique : moyenne pondérée des relations politiques avec les pays connus, modulée par la force de la monnaie.",
    extract: (state) => {
      // Miroir EXACT de check_soft_power_revelation (engine/mod.rs) —
      // pondération 60/40. Avant le 2026-06-10 le front multipliait
      // relation × monnaie : sous une monnaie faible, l'écart avec la
      // valeur observée par le moteur devenait substantiel.
      const known = state.geopolitics.countries.filter(
        (c) => c.known_to_player,
      );
      if (known.length === 0) return 50;
      const meanRelation =
        known.reduce((a, c) => a + c.political_relation, 0) / known.length;
      const currencyFactor = state.economy.currency_strength / 100;
      return Math.max(
        0,
        Math.min(100, meanRelation * 0.6 + 40 * currencyFactor),
      );
    },
    alertBelow: 35,
    revealedFromTurn: 6,
    verdict: (v) =>
      v >= 70
        ? "excellent"
        : v >= 55
          ? "sain"
          : v >= 40
            ? "tenable"
            : v >= 25
              ? "preoccupant"
              : "critique",
  },
  {
    id: "foreign_dependence",
    label: "Dépendance import",
    unit: "/100",
    format: (v) => v.toFixed(0),
    description:
      "Exposition aux ressources contrôlées par des pays étrangers (gaz, pétrole, alimentation, semi-conducteurs, métaux rares). Une valeur haute signifie qu'un partenaire peut nous étrangler.",
    extract: (state) => {
      let totalShare = 0;
      let count = 0;
      for (const c of state.geopolitics.countries) {
        for (const dep of c.dependencies ?? []) {
          totalShare += dep.share;
          count += 1;
        }
      }
      if (count === 0) return 0;
      return Math.max(0, Math.min(100, (totalShare / count) * 100));
    },
    alertAbove: 50,
    revealedFromTurn: 6,
    // Inversé : plus c'est haut, plus c'est risqué.
    verdict: (v) =>
      v <= 15
        ? "excellent"
        : v <= 25
          ? "sain"
          : v <= 35
            ? "tenable"
            : v <= 50
              ? "preoccupant"
              : "critique",
  },
  {
    id: "energy_independence",
    label: "Indépendance énergétique",
    unit: "/100",
    format: (v) => v.toFixed(0),
    description:
      "Part des besoins d'énergie que le pays couvre sans dépendre d'un fournisseur étranger. Un mix importé (gaz, pétrole) expose l'inflation aux chocs mondiaux — et au chantage.",
    extract: (state) => (state.economy.energy?.independence ?? 0.43) * 100,
    alertBelow: 30,
    revealedFromTurn: 8,
    verdict: (v) =>
      v >= 70
        ? "excellent"
        : v >= 55
          ? "sain"
          : v >= 40
            ? "tenable"
            : v >= 30
              ? "preoccupant"
              : "critique",
  },
];

// ---------------------------------------------------------------------------
// Historique — tracking par indicateur
// ---------------------------------------------------------------------------

export interface HistoryEntry {
  turn: number;
  value: number;
}

export type IndicatorHistory = Record<string, HistoryEntry[]>;

// Mandat complet (T0..T60). Avant le sprint lisibilité (2026-06-10) la
// fenêtre était de 12 tours : les trajectoires longues — précisément ce
// que le rééquilibrage économique a rendu intéressant (dérive lente de
// la dette) — disparaissaient de l'historique.
const HISTORY_MAX = 61;

export function emptyHistory(): IndicatorHistory {
  const h: IndicatorHistory = {};
  for (const ind of INDICATORS) {
    h[ind.id] = [];
  }
  return h;
}

export function pushHistory(
  prev: IndicatorHistory,
  state: WorldState,
  aggregates: DerivedAggregates,
): IndicatorHistory {
  const next: IndicatorHistory = { ...prev };
  for (const ind of INDICATORS) {
    const value = ind.extract(state, aggregates);
    const list = [...(next[ind.id] ?? [])];
    list.push({ turn: state.turn, value });
    if (list.length > HISTORY_MAX) list.shift();
    next[ind.id] = list;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Dérivés visuels
// ---------------------------------------------------------------------------

const SPARK_LEVELS = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];

export function sparklineFor(history: HistoryEntry[], width = 4): string {
  if (history.length === 0) return SPARK_LEVELS[3].repeat(width);
  const values = history.map((h) => h.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;

  // Garde les `width` dernières valeurs, padding à gauche avec la plus ancienne.
  const tail = values.slice(-width);
  while (tail.length < width) tail.unshift(tail[0] ?? 0);

  if (range < 1e-9) return SPARK_LEVELS[3].repeat(width);

  return tail
    .map((v) => {
      const level = Math.round(((v - min) / range) * (SPARK_LEVELS.length - 1));
      return SPARK_LEVELS[
        Math.max(0, Math.min(SPARK_LEVELS.length - 1, level))
      ];
    })
    .join("");
}

export function trendFor(history: HistoryEntry[]): {
  direction: TrendDirection;
  delta: number;
} {
  if (history.length < 2) return { direction: "flat", delta: 0 };
  const first = history[0].value;
  const last = history[history.length - 1].value;
  const delta = last - first;
  const epsilon = Math.max(Math.abs(first) * 0.001, 0.01);
  if (Math.abs(delta) < epsilon) return { direction: "flat", delta };
  return { direction: delta > 0 ? "up" : "down", delta };
}

// ---------------------------------------------------------------------------
// Deltas — seuils d'affichage et formatage partagés (strip + rapport de tour)
// ---------------------------------------------------------------------------

/**
 * Seuil en dessous duquel une variation est considérée comme du bruit
 * d'affichage. Source unique partagée par l'IndicatorStrip et le panneau
 * « Ce tour-ci ». Relâchés au sprint lisibilité (2026-06-10) : les anciens
 * seuils (0,5 Md€, 0,05 pt) avalaient les variations des décisions
 * ordinaires — le joueur concluait à tort que ses choix n'avaient aucun
 * effet économique.
 */
export function displayEpsilonFor(def: IndicatorDef): number {
  switch (def.id) {
    case "inflation":
    case "unemployment":
      return 0.0001; // 0,01 point de pourcentage (valeurs en ratio)
    case "gdp":
    case "budget":
      return 0.1e9; // 0,1 Md€
    case "population":
      return 1e3; // 1 000 habitants — la démographie R10 bouge par ~5 k/mois
    default:
      return 0.1; // approbation, dette, indices /100
  }
}

/**
 * Formate une variation signée dans l'unité de l'indicateur, avec des
 * décimales adaptatives (une petite variation réelle reste visible au
 * lieu d'être arrondie à 0). Renvoie `null` sous le seuil de bruit.
 */
export function formatDeltaValue(
  def: IndicatorDef,
  delta: number | null,
): string | null {
  if (delta === null || Math.abs(delta) < displayEpsilonFor(def)) return null;
  const sign = delta > 0 ? "+" : "−";
  const abs = Math.abs(delta);
  if (def.id === "inflation" || def.id === "unemployment") {
    const pts = abs * 100;
    return `${sign}${pts.toFixed(pts < 0.1 ? 2 : 1)} pt`;
  }
  if (def.id === "gdp" || def.id === "budget") {
    const billions = abs / 1e9;
    return `${sign}${billions.toFixed(billions < 10 ? 1 : 0)} Md€`;
  }
  if (def.id === "population") {
    // Les mouvements démographiques mensuels se comptent en milliers
    // d'habitants — illisibles en millions arrondis.
    return abs < 0.1e6
      ? `${sign}${(abs / 1e3).toFixed(0)} k hab`
      : `${sign}${(abs / 1e6).toFixed(2)} M`;
  }
  return `${sign}${abs.toFixed(abs < 1 ? 1 : 0)}${def.unit.trim() === "%" ? " pt" : ""}`;
}

export function formatTrendDelta(
  indicator: IndicatorDef,
  delta: number,
): string {
  if (indicator.unit === "%" || indicator.unit === " %") {
    // Les valeurs sont déjà en unités finales pour approbation (0-100),
    // mais en ratio pour inflation/chômage (0-1). On réutilise `format`.
    return indicator
      .format(delta)
      .replace(/^-?/, (m) => (delta >= 0 ? "+" : m));
  }
  const formatted = indicator.format(delta);
  return delta >= 0 ? `+${formatted.replace(/^\+?/, "")}` : formatted;
}
